import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "../components/Logo";
import { Flame, Trophy, Wallet, MapPin, Zap, ArrowRight, Star, Shield, Sparkles } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1492462543947-040389c4a66c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxoYXBweSUyMHBlcnNvbiUyMHBob25lJTIwY2l0eXxlbnwwfHx8fDE3ODE0NjE3Njd8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div className="min-h-screen">
      {/* Nav */}
      <header className="border-b-2" style={{ borderColor: "var(--cs-ink)" }}>
        <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo size={30} />
          <div className="flex items-center gap-2">
            <Link to="/login" className="brut-btn brut-btn-ghost" data-testid="landing-login-btn">
              Log in
            </Link>
            <Link to="/signup" className="brut-btn brut-btn-money" data-testid="landing-signup-btn">
              Sign up free
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dotgrid opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 py-12 sm:py-20 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7 slide-up">
            <span className="brut-badge mb-5">
              <Sparkles size={12} /> Now live across Tennessee
            </span>
            <h1 className="font-display font-black tracking-tighter leading-[0.95] text-[3.2rem] sm:text-7xl lg:text-[5.5rem]">
              Earn <span className="text-[var(--cs-money-2)]">cash</span>.<br />
              Build a <span className="relative inline-block">
                <span className="text-[var(--cs-flame)]">streak</span>
                <Flame
                  size={48}
                  className="absolute -right-12 top-0 flame-anim text-[var(--cs-flame)]"
                  fill="#FF6B00"
                />
              </span>.
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-[var(--cs-ink-2)] max-w-xl font-medium leading-relaxed">
              Quick local tasks. $5–$30 in 5–30 minutes. Walk a dog, move a box,
              wait in line, pick up groceries. Get paid the same day.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" className="brut-btn brut-btn-money text-base px-6 py-4" data-testid="hero-cta-signup">
                Start earning today <ArrowRight size={18} />
              </Link>
              <Link to="/login" className="brut-btn text-base px-6 py-4" data-testid="hero-cta-login">
                I already have an account
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm font-semibold text-[var(--cs-ink-2)] flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <Shield size={16} className="text-[var(--cs-money-2)]" /> Same-day payouts
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Star size={16} className="text-[var(--cs-flame)]" fill="#FF6B00" /> 4.8 avg rating
              </span>
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={16} className="text-[var(--cs-blue)]" /> Knoxville · Nashville · Chattanooga · Memphis
              </span>
            </div>
          </div>

          {/* Hero card cluster */}
          <div className="lg:col-span-5 relative pop-in">
            <div className="brut-card p-0 overflow-hidden rotate-[-1.5deg]">
              <img
                src={HERO_IMG}
                alt="Earning tasks in the city"
                className="w-full h-72 object-cover"
                style={{ borderBottom: "2px solid var(--cs-ink)" }}
              />
              <div className="p-5 bg-white">
                <div className="text-xs uppercase tracking-widest font-bold text-[var(--cs-ink-2)]">Today's payout</div>
                <div className="font-display tabular text-6xl font-black text-[var(--cs-money-2)] leading-none mt-1">
                  $68
                </div>
                <div className="mt-3 inline-flex items-center gap-2 text-sm font-semibold">
                  <Flame size={16} className="text-[var(--cs-flame)] flame-anim" fill="#FF6B00" />
                  7 day streak — keep it going
                </div>
              </div>
            </div>

            {/* Floating mini card */}
            <div className="absolute -bottom-5 -left-4 sm:-left-8 brut-card p-4 w-56 rotate-[3deg] bg-[var(--cs-yellow)]" data-testid="hero-mini-card">
              <div className="text-[10px] font-bold uppercase tracking-widest">Leaderboard</div>
              <div className="mt-1 flex items-center gap-2">
                <Trophy size={20} className="text-[var(--cs-ink)]" />
                <div>
                  <div className="text-xs font-bold">Top earner</div>
                  <div className="font-display text-lg font-extrabold leading-none">Sam O. — $689</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-y-2 bg-white" style={{ borderColor: "var(--cs-ink)" }}>
        <div className="max-w-7xl mx-auto px-5 py-16">
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mb-2">
            Three steps. Same day cash.
          </h2>
          <p className="text-[var(--cs-ink-2)] text-lg max-w-2xl">A marketplace for the things you'd rather not do yourself — done by people nearby in minutes.</p>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Post or browse", d: "Pick a 5–30 min task nearby, or post one. Set price and time.", icon: MapPin },
              { n: "02", t: "Claim & chat", d: "Realtime chat to coordinate. Show up, get it done.", icon: Zap },
              { n: "03", t: "Cash + streak", d: "Mark complete, rate, get paid. Daily streaks = bonuses.", icon: Flame },
            ].map((s, i) => (
              <div key={i} className="brut-card p-6" data-testid={`step-card-${i}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono-num font-bold text-[var(--cs-ink-2)] text-sm">{s.n}</span>
                  <s.icon size={28} className="text-[var(--cs-money-2)]" />
                </div>
                <h3 className="font-display text-2xl font-extrabold">{s.t}</h3>
                <p className="mt-2 text-[var(--cs-ink-2)]">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-5 py-16">
        <h2 className="font-display text-3xl sm:text-4xl font-black mb-6">What people pay for around here</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {[
            ["Errands", "$5–15", "cat-errands"],
            ["Manual Labor", "$15–30", "cat-manual_labor"],
            ["Pet Care", "$12–25", "cat-pet_care"],
            ["Tech Help", "$15–25", "cat-tech_help"],
            ["Delivery", "$8–20", "cat-delivery"],
          ].map(([n, r, c], i) => (
            <div key={n} className="brut-card p-4 slide-up" style={{ animationDelay: `${i * 60}ms` }}>
              <span className={`brut-chip ${c}`}>{n}</span>
              <div className="mt-3 font-display text-xl font-extrabold tabular">{r}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA bar */}
      <section className="bg-[var(--cs-ink)] text-white">
        <div className="max-w-7xl mx-auto px-5 py-14 flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between">
          <div>
            <h3 className="font-display text-3xl sm:text-4xl font-black tracking-tight">
              Your first task fee? On us.
            </h3>
            <p className="text-slate-300 mt-2">Sign up with a referral code and you both earn $5 on your first completion.</p>
          </div>
          <Link to="/signup" className="brut-btn brut-btn-money text-base px-6 py-4" data-testid="bottom-cta-signup">
            Get started <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <footer className="py-8 text-center text-xs text-[var(--cs-ink-2)]">
        © {new Date().getFullYear()} CashStreak · Built for hustlers.
        <span className="mx-2">·</span>
        <Link to="/privacy" className="underline" data-testid="footer-privacy">Privacy</Link>
        <span className="mx-2">·</span>
        <Link to="/terms" className="underline" data-testid="footer-terms">Terms</Link>
      </footer>
    </div>
  );
}
